import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  DollarSign, 
  UserCheck, 
  ClipboardList,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Plus,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatCurrency, formatNumber, getStatusColor, cn } from '../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-emerald-500',
    yellow: 'bg-amber-500',
    purple: 'bg-violet-500',
    teal: 'bg-teal-500'
  };

  return (
    <div className={cn('rounded-xl p-6 text-white relative overflow-hidden', colorClasses[color])} data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-white/80 text-sm font-medium">{title}</p>
          <h3 className="text-3xl font-bold mt-1">{value}</h3>
        </div>
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/80">{subtitle}</span>
        <ChevronRight className="w-5 h-5 text-white/60" />
      </div>
      {trend && (
        <div className="absolute top-4 right-14 flex items-center gap-1 text-xs">
          {trend === 'up' ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span>{trendValue}</span>
        </div>
      )}
    </div>
  );
};

const COLORS = ['#10B981', '#3B82F6', '#EF4444'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [feesOverview, setFeesOverview] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, feesRes, attendanceRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/fees-overview`),
        axios.get(`${API_URL}/dashboard/attendance-chart`),
        axios.get(`${API_URL}/students?limit=5`)
      ]);

      setStats(statsRes.data);
      setFeesOverview(feesRes.data);
      setAttendanceData(attendanceRes.data);
      setStudents(studentsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API_URL}/seed-data`);
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to seed data:', error);
    }
  };

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.admission_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const feePieData = feesOverview ? [
    { name: 'Collected', value: feesOverview.collected },
    { name: 'Pending', value: feesOverview.pending },
    { name: 'Overdue', value: feesOverview.overdue },
  ] : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Students"
          value={stats?.total_students || 0}
          subtitle="Jan-Dec 2024"
          icon={Users}
          color="blue"
          trend="up"
          trendValue="+3%"
        />
        <StatCard
          title="Total Fees Collected"
          value={formatCurrency(stats?.total_fees_collected || 0)}
          subtitle={`+ ${formatCurrency(350000)}`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Today's Attendance"
          value={`${stats?.todays_attendance_percent || 0}%`}
          subtitle={`${stats?.absent_count || 0} Absent`}
          icon={UserCheck}
          color="yellow"
        />
        <StatCard
          title="Upcoming Exams"
          value={stats?.upcoming_exams || 0}
          subtitle="View"
          icon={ClipboardList}
          color="purple"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Student List */}
        <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="student-list-card">
          <div className="p-6 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-900">Student List</h2>
              <Button 
                onClick={() => navigate('/students/new')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="add-student-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Student
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-2 flex-1">
                <Button variant="outline" size="icon" className="shrink-0">
                  <Filter className="w-4 h-4" />
                </Button>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-students"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Admission No</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Stream</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-700">{student.admission_no}</td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{student.name}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{student.class_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{student.stream || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
                          getStatusColor(student.status)
                        )}>
                          {student.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="text-slate-500">
                        <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="font-medium">No students found</p>
                        <p className="text-sm mt-1">Add students or seed sample data</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={seedData}
                          data-testid="seed-data-btn"
                        >
                          Seed Sample Data
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredStudents.length > 0 && (
            <div className="p-4 border-t border-slate-200">
              <Button 
                variant="link" 
                className="text-indigo-600 hover:text-indigo-700"
                onClick={() => navigate('/students')}
                data-testid="view-all-students"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="lg:col-span-5 space-y-6">
          {/* Fees Overview */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="fees-overview-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Fees Overview</h2>
              <Button 
                variant="link" 
                className="text-indigo-600 hover:text-indigo-700 px-0"
                onClick={() => navigate('/fees')}
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={feePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {feePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-xs text-slate-500">Collected</span>
                </div>
                <p className="font-semibold text-slate-900 text-sm">{formatCurrency(feesOverview?.collected || 0)}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-xs text-slate-500">Pending</span>
                </div>
                <p className="font-semibold text-slate-900 text-sm">{formatCurrency(feesOverview?.pending || 0)}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-slate-500">Overdue</span>
                </div>
                <p className="font-semibold text-slate-900 text-sm">{formatCurrency(feesOverview?.overdue || 0)}</p>
              </div>
            </div>
          </div>

          {/* Recent Attendance */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="attendance-chart-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Recent Attendance</h2>
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={attendanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="present" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                    name="Present"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="absent" 
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={{ fill: '#F59E0B', strokeWidth: 2 }}
                    name="Absent"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-slate-600">Present</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-sm text-slate-600">Absent</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="quick-actions">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-200"
            onClick={() => navigate('/students/new')}
            data-testid="quick-add-student"
          >
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium">Add Student</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-200"
            onClick={() => navigate('/fees')}
            data-testid="quick-record-payment"
          >
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm font-medium">Record Payment</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-200"
            onClick={() => navigate('/attendance')}
            data-testid="quick-mark-attendance"
          >
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium">Mark Attendance</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-amber-50 hover:border-amber-200"
            onClick={() => navigate('/exams')}
            data-testid="quick-view-exams"
          >
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-sm font-medium">View Exams</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
